
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useOptimizedAuth } from '@/hooks/useOptimizedAuth';
import { supabase } from '@/integrations/supabase/client';

export interface Notification {
  id: string;
  title: string;
  message: string;
  type: 'info' | 'success' | 'warning' | 'error';
  read: boolean;
  created_at: string;
  action_url?: string;
  image_url?: string;
}

export interface NotificationContextType {
  notifications: Notification[];
  unreadCount: number;
  hasPermission: boolean;
  error: string | null;
  markAsRead: (id: string) => void;
  markAllAsRead: () => void;
  addNotification: (notification: Omit<Notification, 'id' | 'created_at' | 'read'>) => void;
  removeNotification: (id: string) => void;
  clearAll: () => void;
  requestPermission: () => Promise<boolean>;
  retryConnection: () => void;
  isLoading: boolean;
}

const ConsolidatedNotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const ConsolidatedNotificationProvider = ({ children }: { children: ReactNode }) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasPermission, setHasPermission] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { user } = useOptimizedAuth();

  const unreadCount = notifications.filter(n => !n.read).length;

  const markAsRead = (id: string) => {
    setNotifications(prev =>
      prev.map(n => (n.id === id ? { ...n, read: true } : n))
    );
  };

  const markAllAsRead = () => {
    setNotifications(prev =>
      prev.map(n => ({ ...n, read: true }))
    );
  };

  const addNotification = (notification: Omit<Notification, 'id' | 'created_at' | 'read'>) => {
    const newNotification: Notification = {
      ...notification,
      id: Date.now().toString(),
      created_at: new Date().toISOString(),
      read: false,
    };
    setNotifications(prev => [newNotification, ...prev]);
  };

  const removeNotification = (id: string) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  };

  const clearAll = () => {
    setNotifications([]);
  };

  const requestPermission = async (): Promise<boolean> => {
    try {
      const permission = await Notification.requestPermission();
      setHasPermission(permission === 'granted');
      return permission === 'granted';
    } catch (error) {
      setError('Failed to request notification permission');
      return false;
    }
  };

  const retryConnection = () => {
    setError(null);
    // Implement retry logic if needed
  };

  // Load notifications for authenticated users
  useEffect(() => {
    if (user) {
      setIsLoading(true);
      // Simulate loading notifications
      setTimeout(() => {
        setNotifications([]);
        setIsLoading(false);
      }, 100);
    } else {
      setNotifications([]);
    }
  }, [user]);

  const value: NotificationContextType = {
    notifications,
    unreadCount,
    hasPermission,
    error,
    markAsRead,
    markAllAsRead,
    addNotification,
    removeNotification,
    clearAll,
    requestPermission,
    retryConnection,
    isLoading,
  };

  return (
    <ConsolidatedNotificationContext.Provider value={value}>
      {children}
    </ConsolidatedNotificationContext.Provider>
  );
};

export const useConsolidatedNotifications = () => {
  const context = useContext(ConsolidatedNotificationContext);
  if (context === undefined) {
    throw new Error('useConsolidatedNotifications must be used within a ConsolidatedNotificationProvider');
  }
  return context;
};
